import { useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Collapsible,
  CollapsiblePanel,
  Field,
  FieldLabel,
  Input,
  OptionSelect,
  Separator,
  Switch,
} from "@salestrak/ui";

import { EntityGroupTag } from "#app/components/entity";
import { WorkflowSelector } from "#app/components/entity-selectors/workflow-selector";
import {
  IssueTypesPicker,
  NO_OFFER,
  SubmissionLimitField,
  without,
  type FormSettingsChange,
} from "#app/components/forms/form-builder/form-settings-pane";
import {
  PageHeader,
  PageHeaderTitle,
  SETTINGS_CONTENT_MAX_WIDTH,
  SettingsPageGutter,
} from "#app/components/page-header";
import {
  SettingsGroup,
  SettingsGroupContent,
  SettingsGroupRow,
} from "#app/components/settings/settings-group";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { ENTITY_KIND_ICON, entityFromMemberOrTeam, type EntityData } from "#app/lib/entity-visuals";
import {
  DEFAULT_SUBMIT_LABEL,
  isOfferedAnywhere,
  type Form,
  type FormOffer,
} from "#app/lib/forms";

const VISIBILITY_OPTIONS = [
  { label: "Private", value: "private" },
  { label: "Public", value: "public" },
];

const OFFER_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Some", value: "some" },
];

// Copy names no record kind outside Offered on; forms will be offered on more than Projects.
export function FormSettingsPage({
  form,
  onChange,
  onRequiredByDefaultChange,
  onShare,
}: {
  form: Pick<Form, "formId" | "status" | "settings" | "requiredByDefault" | "sharedWith">;
  onChange: FormSettingsChange;
  onRequiredByDefaultChange: (requiredByDefault: boolean) => void;
  onShare: () => void;
}) {
  const settings = form.settings ?? {};
  const staged = settings.visibility ?? "private";
  const offer = settings.offeredOn;
  const once = settings.submissionLimit === 1;
  const setOffer = (next: FormOffer) =>
    onChange("offeredOn", (current) => ({ ...current, offeredOn: next }));

  const maps = useWorkspaceData();
  const shared = useMemo<EntityData[]>(
    () =>
      (form.sharedWith ?? []).flatMap((share) => {
        const entity = entityFromMemberOrTeam(maps, share.id);
        return entity ? [entity] : [];
      }),
    [maps, form.sharedWith],
  );

  return (
    <Box background="background" direction="column" duration={100} enter="fade" fill="flex" minHeight={0} overflow="auto">
      <PageHeader centered>
        <PageHeaderTitle>Settings</PageHeaderTitle>
      </PageHeader>
      <Box align="center" direction="column" paddingX="md" paddingY="md">
        <Box direction="column" fill="width" gap="2xl" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
          <SettingsGroup label="Offered on">
            <SettingsGroupRow
              icon={ENTITY_KIND_ICON.issue}
              label="Projects"
              subtitle={
                !offer ? undefined : isOfferedAnywhere({ settings }) ? "Any listed workflow or type" : "None yet"
              }
            >
              <OptionSelect
                aria-label="Offered on"
                onValueChange={(value) =>
                  onChange("offeredOn", (current) =>
                    value === "some"
                      ? { ...current, offeredOn: current.offeredOn ?? NO_OFFER }
                      : without(current, "offeredOn"),
                  )
                }
                options={OFFER_OPTIONS}
                value={offer ? "some" : "all"}
              />
            </SettingsGroupRow>
            {offer ? (
              <SettingsGroupContent>
                <Box direction="column" gap="lg">
                  <Field fill>
                    <FieldLabel>Workflows</FieldLabel>
                    <WorkflowSelector
                      chips
                      fill
                      maxSelections={0}
                      onValueChange={(workflowIds) => setOffer({ ...offer, workflowIds })}
                      placeholder="Add workflows..."
                      value={offer.workflowIds}
                    />
                  </Field>
                  <Field fill>
                    <FieldLabel>Types</FieldLabel>
                    <IssueTypesPicker
                      onValueChange={(issueTypeIds) => setOffer({ ...offer, issueTypeIds })}
                      value={offer.issueTypeIds}
                    />
                  </Field>
                </Box>
              </SettingsGroupContent>
            ) : null}
          </SettingsGroup>

          <SettingsGroup label="Submitting">
            <SettingsGroupRow icon="repeat" label="Submission limit">
              <Box width={220}>
                <SubmissionLimitField onChange={onChange} settings={settings} />
              </Box>
            </SettingsGroupRow>
            <SettingsGroupRow
              icon="redo"
              label="Submit another"
              subtitle={once ? "Off when limited to one" : "Offered after submitting"}
            >
              <Switch
                aria-label="Submit another"
                checked={settings.resubmit === true && !once}
                disabled={once}
                onCheckedChange={(next) =>
                  onChange("resubmit", (current) =>
                    next ? { ...current, resubmit: true } : without(current, "resubmit"),
                  )
                }
              />
            </SettingsGroupRow>
            <SettingsGroupRow
              icon="send"
              label="Submit button text"
            >
              <Box width={220}>
                <Input
                  aria-label="Submit button text"
                  maxLength={40}
                  onChange={(event) => {
                    const submitLabel = event.target.value;
                    onChange("submitLabel", (current) =>
                      submitLabel ? { ...current, submitLabel } : without(current, "submitLabel"),
                    );
                  }}
                  placeholder={DEFAULT_SUBMIT_LABEL}
                  value={settings.submitLabel ?? ""}
                />
              </Box>
            </SettingsGroupRow>
          </SettingsGroup>

          <SettingsGroup label="Submissions">
            {/* One entry so Shared with folds away with its divider when Public. */}
            <Box>
              <SettingsGroupRow
                icon={staged === "public" ? "globe" : "lock"}
                label="Who can see submissions"
                subtitle={
                  staged === "public"
                    ? "Anyone can see its submissions"
                    : "Only whoever filled it out, people it's shared with, and admins"
                }
              >
                <OptionSelect
                  aria-label="Who can see submissions"
                  onValueChange={(value) =>
                    onChange("visibility", (current) =>
                      value === "public"
                        ? { ...current, visibility: "public" }
                        : without(current, "visibility"),
                    )
                  }
                  options={VISIBILITY_OPTIONS}
                  value={staged}
                />
              </SettingsGroupRow>
              <Collapsible open={staged === "private"}>
                <CollapsiblePanel>
                  <Box paddingX="md">
                    <Separator />
                  </Box>
                  <SettingsGroupRow
                    icon="share"
                    label="Shared with"
                    labelAccessory={
                      shared.length > 0 ? (
                        <EntityGroupTag entities={shared} max={4} size="xs" />
                      ) : (
                        <Badge tone="outline">Nobody</Badge>
                      )
                    }
                  >
                    <Button onClick={onShare} variant="ghost">
                      Share
                    </Button>
                  </SettingsGroupRow>
                </CollapsiblePanel>
              </Collapsible>
            </Box>
          </SettingsGroup>

          <SettingsGroup label="Defaults">
            <SettingsGroupRow
              icon="list-checks"
              label="New questions are required"
              subtitle="Questions you add from now on start Required."
            >
              <Switch
                aria-label="New questions are required"
                checked={form.requiredByDefault === true}
                onCheckedChange={onRequiredByDefaultChange}
              />
            </SettingsGroupRow>
          </SettingsGroup>
          <SettingsPageGutter />
        </Box>
      </Box>
    </Box>
  );
}
